/**
 * Dashboard API client.
 *
 * Agents authenticate with a short-lived access token (15m) plus a refresh
 * token (30d). A 401 triggers exactly one refresh attempt, and concurrent 401s
 * share that single refresh rather than stampeding the endpoint.
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');
const ROOT = `${API_BASE}/api/v1`;

const STORAGE_KEY = 'superchat:auth';

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function loadTokens() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
  } catch {
    return null;
  }
}

export function saveTokens(tokens) {
  if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(STORAGE_KEY);
}

let refreshInFlight = null;
let onAuthLost = () => {};

export function setAuthLostHandler(handler) {
  onAuthLost = handler;
}

async function refreshTokens() {
  const stored = loadTokens();
  if (!stored?.refreshToken) throw new ApiError(401, 'no_refresh_token', 'Session expired');

  // One refresh at a time; every waiter gets the same result.
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${ROOT}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: stored.refreshToken }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || payload?.ok === false) {
        throw new ApiError(401, 'refresh_failed', 'Session expired');
      }
      const tokens = {
        accessToken: payload.data.accessToken,
        refreshToken: payload.data.refreshToken,
        user: payload.data.user,
      };
      saveTokens(tokens);
      return tokens;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Forces a token refresh from outside the REST path. The socket needs this:
 * its handshake carries the access token, but a rejected handshake is not a
 * 401 response and so never reaches the retry logic in `request`.
 */
export async function forceRefresh() {
  try {
    return await refreshTokens();
  } catch (err) {
    saveTokens(null);
    onAuthLost();
    throw err;
  }
}

export async function request(
  path,
  { method = 'GET', body, query, auth = true, retry = true } = {}
) {
  const url = new URL(`${ROOT}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const tokens = loadTokens();
    if (tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`;
  }

  let res;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiError(0, 'network_error', 'Cannot reach the API', { cause: String(cause) });
  }

  if (res.status === 401 && auth && retry) {
    try {
      await refreshTokens();
      return request(path, { method, body, query, auth, retry: false });
    } catch (err) {
      saveTokens(null);
      onAuthLost();
      throw err;
    }
  }

  if (res.status === 204) return { data: null, meta: null };

  const payload = await res.json().catch(() => null);

  if (!res.ok || payload?.ok === false) {
    const error = payload?.error ?? {};
    throw new ApiError(
      res.status,
      error.code ?? 'http_error',
      error.message ?? `Request failed (${res.status})`,
      error.details
    );
  }

  return { data: payload?.data ?? null, meta: payload?.meta ?? null };
}

const get = async (path, query) => (await request(path, { query })).data;
const page = (path, query) => request(path, { query });

/* ----------------------------------------------------------------- surface */

export const api = {
  config: () => get('/config'),

  auth: {
    login: (email, password) =>
      request('/auth/login', { method: 'POST', auth: false, body: { email, password } }).then(
        (r) => r.data
      ),
    register: (email, password, name) =>
      request('/auth/register', {
        method: 'POST',
        auth: false,
        body: { email, password, name },
      }).then((r) => r.data),
    me: () => get('/auth/me'),
    logout: () => request('/auth/logout', { method: 'POST' }),
    updateProfile: (patch) => request('/auth/me', { method: 'PATCH', body: patch }).then((r) => r.data),

    /* The four below are reached from an emailed link, so none of them may
       require an existing session — `auth: false` keeps the client from
       attaching (or trying to refresh) a token that is not there. */
    verifyEmail: (token) =>
      request('/auth/verify-email', { method: 'POST', auth: false, body: { token } }).then(
        (r) => r.data
      ),
    /**
     * Composer shortcuts (`/known-issue`). Every mutation answers with the
     * whole list, so the client never has to reconcile a partial update.
     */
    shortcuts: {
      list: () => get('/auth/me/shortcuts'),
      create: (shortcut) =>
        request('/auth/me/shortcuts', { method: 'POST', body: shortcut }).then((r) => r.data),
      update: (id, patch) =>
        request(`/auth/me/shortcuts/${id}`, { method: 'PATCH', body: patch }).then((r) => r.data),
      remove: (id) =>
        request(`/auth/me/shortcuts/${id}`, { method: 'DELETE' }).then((r) => r.data),
      restoreDefaults: () =>
        request('/auth/me/shortcuts/restore-defaults', { method: 'POST' }).then((r) => r.data),
    },

    resendVerification: () =>
      request('/auth/resend-verification', { method: 'POST' }).then((r) => r.data),
    forgotPassword: (email) =>
      request('/auth/forgot-password', { method: 'POST', auth: false, body: { email } }).then(
        (r) => r.data
      ),
    resetPassword: (token, password) =>
      request('/auth/reset-password', {
        method: 'POST',
        auth: false,
        body: { token, password },
      }).then((r) => r.data),
  },

  /**
   * Vouching for an installed app that has no password field of its own.
   *
   * The desktop notifier starts the grant; this browser — where the session
   * already is — is what attaches an identity to it.
   */
  device: {
    preview: (userCode) => get(`/auth/device/${encodeURIComponent(userCode)}`),
    approve: (userCode) =>
      request('/auth/device/approve', { method: 'POST', body: { userCode } }).then((r) => r.data),
    deny: (userCode) =>
      request('/auth/device/deny', { method: 'POST', body: { userCode } }).then((r) => r.data),
  },

  invites: {
    /**
     * Shares any number of apps in one invitation.
     * `grants` is `[{ appId, permissions }]` — the permissions are per app,
     * because that is how they are stored and enforced.
     */
    create: (email, grants) =>
      request('/invites', { method: 'POST', body: { email, grants } }).then((r) => r.data),
    /** Public preview, so the accept page can say what is on offer. */
    preview: (token) =>
      request(`/invites/${encodeURIComponent(token)}`, { auth: false }).then((r) => r.data),
    accept: (token) =>
      request(`/invites/${encodeURIComponent(token)}/accept`, { method: 'POST' }).then(
        (r) => r.data
      ),
  },

  /**
   * Handing an app to someone else. Two steps, each carrying the password:
   * `start` returns a one-time challenge that `confirm` must echo back, so the
   * confirmation cannot happen anywhere but the browser that was warned.
   */
  transfer: {
    pending: (appId) => get(`/apps/${appId}/transfer`),
    start: (appId, toUserId, password) =>
      request(`/apps/${appId}/transfer`, {
        method: 'POST',
        body: { toUserId, password },
      }).then((r) => r.data),
    confirm: (appId, challenge, password) =>
      request(`/apps/${appId}/transfer/confirm`, {
        method: 'POST',
        body: { challenge, password },
      }).then((r) => r.data),
    cancel: (appId) => request(`/apps/${appId}/transfer`, { method: 'DELETE' }),
  },

  members: {
    /** Roster plus pending invites; `canManage` says whether to show editors. */
    list: (appId) => get(`/apps/${appId}/members`),
    invite: (appId, email, permissions) =>
      request(`/apps/${appId}/members/invites`, {
        method: 'POST',
        body: { email, permissions },
      }).then((r) => r.data),
    revokeInvite: (appId, inviteId) =>
      request(`/apps/${appId}/members/invites/${inviteId}`, { method: 'DELETE' }),
    update: (appId, userId, permissions) =>
      request(`/apps/${appId}/members/${userId}`, {
        method: 'PATCH',
        body: { permissions },
      }).then((r) => r.data),
    remove: (appId, userId) =>
      request(`/apps/${appId}/members/${userId}`, { method: 'DELETE' }),
  },

  apps: {
    list: () => get('/apps'),
    create: (payload) => request('/apps', { method: 'POST', body: payload }).then((r) => r.data),
    get: (appId) => get(`/apps/${appId}`),
    update: (appId, patch) =>
      request(`/apps/${appId}`, { method: 'PATCH', body: patch }).then((r) => r.data),
    rotateKey: (appId) =>
      request(`/apps/${appId}/rotate-key`, { method: 'POST' }).then((r) => r.data),
    members: (appId) => get(`/apps/${appId}/members`),
    installSnippet: (appId) => get(`/apps/${appId}/install-snippet`),
  },

  conversations: {
    list: (appId, query) => page(`/apps/${appId}/conversations`, query),
    get: (appId, id) => get(`/apps/${appId}/conversations/${id}`),
    update: (appId, id, patch) =>
      request(`/apps/${appId}/conversations/${id}`, { method: 'PATCH', body: patch }).then(
        (r) => r.data
      ),
    messages: (appId, id, query) => page(`/apps/${appId}/conversations/${id}/messages`, query),
    send: (appId, id, payload) =>
      request(`/apps/${appId}/conversations/${id}/messages`, {
        method: 'POST',
        body: payload,
      }).then((r) => r.data),
    /**
     * Rewriting or withdrawing something an agent already sent. Both answer
     * with the message in its new shape — a withdrawn one comes back as a
     * tombstone rather than a 204, because the thread still has to render
     * something where the bubble was.
     */
    editMessage: (appId, id, messageId, body) =>
      request(`/apps/${appId}/conversations/${id}/messages/${messageId}`, {
        method: 'PATCH',
        body: { body },
      }).then((r) => r.data),
    deleteMessage: (appId, id, messageId) =>
      request(`/apps/${appId}/conversations/${id}/messages/${messageId}`, {
        method: 'DELETE',
      }).then((r) => r.data),

    /**
     * Translation of last resort, for a browser with no translator of its own.
     * Answers `{ text, sourceLanguage }` or `{ alreadyEnglish: true }` — the
     * same shape the on-device path returns, so the bubble does not care which
     * one answered it.
     */
    translateMessage: (appId, id, messageId) =>
      request(`/apps/${appId}/conversations/${id}/messages/${messageId}/translate`, {
        method: 'POST',
      }).then((r) => r.data),

    markRead: (appId, id) =>
      request(`/apps/${appId}/conversations/${id}/read`, { method: 'POST' }).then((r) => r.data),
    events: (appId, id, query) => page(`/apps/${appId}/conversations/${id}/events`, query),

    /**
     * The visitor's IP and raw user agent.
     *
     * Its own request rather than part of the conversation, because it is the
     * one part not everybody with `chat: read` may see — a read-only member
     * gets a 403 here, which the rail reads as "hide the row".
     */
    identity: (appId, id) => get(`/apps/${appId}/conversations/${id}/identity`),
  },

  tickets: {
    list: (appId, query) => page(`/apps/${appId}/tickets`, query),
    create: (appId, payload) =>
      request(`/apps/${appId}/tickets`, { method: 'POST', body: payload }).then((r) => r.data),
    update: (appId, ticketId, patch) =>
      request(`/apps/${appId}/tickets/${ticketId}`, { method: 'PATCH', body: patch }).then(
        (r) => r.data
      ),
    remove: (appId, ticketId) =>
      request(`/apps/${appId}/tickets/${ticketId}`, { method: 'DELETE' }),

    /**
     * Evidence on a ticket. Additive rather than a whole-array PATCH, so two
     * agents filing a screenshot on the same ticket do not overwrite each
     * other. Both answer with the updated ticket.
     */
    attach: (appId, ticketId, attachments) =>
      request(`/apps/${appId}/tickets/${ticketId}/attachments`, {
        method: 'POST',
        body: { attachments },
      }).then((r) => r.data),
    detach: (appId, ticketId, key) =>
      request(`/apps/${appId}/tickets/${ticketId}/attachments`, {
        method: 'DELETE',
        query: { key },
      }).then((r) => r.data),
  },

  /**
   * Attachments. Nothing here carries file bytes: `sign` asks for a presigned
   * PUT the browser uploads to directly, and `link` trades an object key for a
   * short-lived read URL. See lib/upload.js for the two-step dance.
   */
  uploads: {
    limits: (appId) => get(`/apps/${appId}/uploads/limits`),
    sign: (appId, payload) =>
      request(`/apps/${appId}/uploads`, { method: 'POST', body: payload }).then((r) => r.data),
    link: (appId, key, { download = false } = {}) =>
      get(`/apps/${appId}/uploads/link`, { key, download: download ? 'true' : 'false' }),
  },

  docs: {
    list: (appId, query) => page(`/apps/${appId}/docs`, query),
    create: (appId, payload) =>
      request(`/apps/${appId}/docs`, { method: 'POST', body: payload }).then((r) => r.data),
    update: (appId, docId, patch) =>
      request(`/apps/${appId}/docs/${docId}`, { method: 'PATCH', body: patch }).then((r) => r.data),
    remove: (appId, docId) => request(`/apps/${appId}/docs/${docId}`, { method: 'DELETE' }),
  },

  analytics: {
    overview: (appId, query) => get(`/apps/${appId}/analytics/overview`, query),
    messages: (appId, query) => get(`/apps/${appId}/analytics/messages`, query),
    firstResponse: (appId, query) => get(`/apps/${appId}/analytics/first-response`, query),
    resolution: (appId, query) => get(`/apps/${appId}/analytics/resolution`, query),
    tickets: (appId, query) => get(`/apps/${appId}/analytics/tickets`, query),
  },

  push: {
    publicKey: (appId) => get(`/apps/${appId}/push/public-key`),
    subscribe: (appId, subscription) =>
      request(`/apps/${appId}/push/subscribe`, { method: 'POST', body: subscription }).then(
        (r) => r.data
      ),
    unsubscribe: (appId, endpoint) =>
      request(`/apps/${appId}/push/unsubscribe`, { method: 'POST', body: { endpoint } }),
    subscriptions: (appId) => get(`/apps/${appId}/push/subscriptions`),
  },

  notify: {
    /** Broadcasts a test notification to every visitor on this app. */
    visitors: (appId, isEmergency = false) =>
      request(`/apps/${appId}/notify/visitors`, {
        method: 'POST',
        body: { isEmergency },
      }).then((r) => r.data),

    /** Sends one test email to the signed-in agent's own address. */
    emailTest: (appId) =>
      request(`/apps/${appId}/notify/email-test`, { method: 'POST' }).then((r) => r.data),
  },

  slack: {
    get: (appId) => get(`/apps/${appId}/slack`),
    /** The bot token is write-only: it goes up here and never comes back. */
    connect: (appId, body) =>
      request(`/apps/${appId}/slack`, { method: 'PUT', body }).then((r) => r.data),
    setEvents: (appId, events) =>
      request(`/apps/${appId}/slack`, { method: 'PATCH', body: { events } }).then((r) => r.data),
    disconnect: (appId) => request(`/apps/${appId}/slack`, { method: 'DELETE' }),
    test: (appId) => request(`/apps/${appId}/slack/test`, { method: 'POST' }).then((r) => r.data),
  },

  /**
   * The app's own Resend account, used for mail *to visitors and merchants*.
   *
   * Not the same channel as `notify.emailTest`, which exercises the
   * deployment's own Resend and reaches agents. The API key here is write-only:
   * it goes up and never comes back.
   */
  resend: {
    get: (appId) => get(`/apps/${appId}/resend`),
    connect: (appId, body) =>
      request(`/apps/${appId}/resend`, { method: 'PUT', body }).then((r) => r.data),
    disconnect: (appId) => request(`/apps/${appId}/resend`, { method: 'DELETE' }),
    test: (appId, to) =>
      request(`/apps/${appId}/resend/test`, { method: 'POST', body: to ? { to } : {} }).then(
        (r) => r.data
      ),
  },

  presence: {
    list: (appId) => get(`/apps/${appId}/presence`),
  },

  /** The agent's recent notifications on one app, newest first, by cursor. */
  notifications: {
    list: (appId, query) => page(`/apps/${appId}/notifications`, query),
  },

  /**
   * Pinning a thread to the top of your own inbox.
   *
   * Its own path rather than a field on the conversation PATCH, because it is
   * gated differently: a read-only member may pin, but may not edit the
   * conversation. Both answer with the whole conversation, so the caller can
   * drop it straight into the list.
   */
  pins: {
    add: (appId, conversationId) =>
      request(`/apps/${appId}/pins/${conversationId}`, { method: 'PUT' }).then((r) => r.data),
    remove: (appId, conversationId) =>
      request(`/apps/${appId}/pins/${conversationId}`, { method: 'DELETE' }).then((r) => r.data),
  },

  /**
   * Whether push and Slack notifications actually arrived.
   *
   * `meta.stats` carries the counts; the rows are there to be narrowed to the
   * failures with `problems: true`, which is the only view anyone opens this
   * for. `retry` re-sends one that gave up.
   */
  deliveries: {
    list: (appId, query) => page(`/apps/${appId}/deliveries`, query),
    retry: (appId, deliveryId) =>
      request(`/apps/${appId}/deliveries/${deliveryId}/retry`, { method: 'POST' }).then(
        (r) => r.data
      ),
  },
};

export { API_BASE };
export default api;
